import styled from "styled-components";
import { FaFilePen, FaPlus } from 'react-icons/fa6';
import { useState, useRef, useMemo } from 'react';
import { Button, Space, Table, Input, Tag } from "antd";
import { DataEditor } from "./DataEditor";
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import EditRoleEditor from "./EditRoleEditor";

function CustomerRoleTable({ customerRoles, organizationId, customerId, fetchCustomerRoles, loading, availableModules }) {
    const [isDataEditorOpen, setIsDataEditorOpen] = useState(false);
    const [isEditRoleEditorOpen, setIsEditRoleEditorOpen] = useState(false);
    const [editableRole, setEditableRole] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    const existingRoleNames = useMemo(() => (customerRoles || []).map((role) => role.Name), [customerRoles]);
    const moduleFilterOptions = useMemo(() => {
        const moduleSet = new Set();
        (customerRoles || []).forEach((role) => {
            const modules = (role.Modules || '')
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean);
            modules.forEach((module) => moduleSet.add(module));
        });

        return Array.from(moduleSet)
            .sort((a, b) => a.localeCompare(b))
            .map((module) => ({ text: module, value: module }));
    }, [customerRoles]);

    const openEditorToAdd = () => {
        if (organizationId && customerId) {
            setIsDataEditorOpen(true);
        } else {
            alert("Select organization and customer first.");
        }
    };

    const openEditorToEdit = (record) => {
        setEditableRole(record);
        setIsEditRoleEditorOpen(true);
    };

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns = [
        {
            title: 'Role Name',
            dataIndex: 'Name',
            key: 'Name',
            sorter: (a, b) => a.Name.toString().localeCompare(b.Name.toString()),
            ...getColumnSearchProps('Name'),
        },
        {
            title: 'Status',
            dataIndex: 'Status',
            key: 'Status',
            filters: [
                { text: 'Active', value: 1 },
                { text: 'Inactive', value: 0 }
            ],
            onFilter: (value, record) => Number(record.Status) === Number(value),
            render: (_, record) => (
                <Tag color={record.Status ? 'green' : 'default'}>
                    {record.Status ? 'Active' : 'Inactive'}
                </Tag>
            )
        },
        {
            title: 'Modules',
            dataIndex: 'Modules',
            key: 'Modules',
            filters: moduleFilterOptions,
            onFilter: (value, record) => {
                const modules = (record.Modules || '')
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean);
                return modules.includes(String(value));
            },
            render: (modulesText) => modulesText || '-',
        },
        {
            title: (
                <Button
                    color="default"
                    variant="text"
                    onClick={openEditorToAdd}
                    icon={<FaPlus style={{ color: '#ffad1c' }} />}
                />
            ),
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Button
                    color="default"
                    variant="text"
                    onClick={(e) => {
                        e.stopPropagation();
                        openEditorToEdit(record);
                    }}
                    icon={<FaFilePen style={{ color: '#ffad1c' }} />}
                />
            )
        },
    ];

    const handleDataEditFinished = async () => {
        setIsDataEditorOpen(false);
        await fetchCustomerRoles({ customer_id: customerId });
    };

    const handleDataEditCancelled = () => {
        setIsDataEditorOpen(false);
    };

    const handleEditRoleFinished = async () => {
        setIsEditRoleEditorOpen(false);
        setEditableRole(null);
        await fetchCustomerRoles({ customer_id: customerId });
    };

    const handleEditRoleCancelled = () => {
        setIsEditRoleEditorOpen(false);
        setEditableRole(null);
    };

    return (
        <TableContainer>
            <Table
                scroll={{ x: 100 }}
                columns={columns}
                rowKey={(record) => record.Id}
                dataSource={customerRoles || []}
                loading={loading}
            />
            {isDataEditorOpen ? (
                <DataEditor
                    isOpen={isDataEditorOpen}
                    dataItem={{ organizationId, customerId }}
                    onDataEditorFinished={handleDataEditFinished}
                    onDataEditorCancelled={handleDataEditCancelled}
                    availableModules={availableModules}
                    existingRoleNames={existingRoleNames}
                />
            ) : null}
            {isEditRoleEditorOpen && editableRole ? (
                <EditRoleEditor
                    isOpen={isEditRoleEditorOpen}
                    role={editableRole}
                    availableModules={availableModules}
                    onFinished={handleEditRoleFinished}
                    onCancelled={handleEditRoleCancelled}
                />
            ) : null}
        </TableContainer>
    );
}

const TableContainer = styled.div`
    border-radius: 0px;
    margin: 20px;
    margin-top: -20px;
    width: 100%;
    margin-left: 0;
`;

export default CustomerRoleTable;
